import { DEFAULT_TENANT_THEME } from './defaultTenantTheme';
import { validateTenantTheme } from './themeSchema';
import { logger } from '../utils/logger';

const EXTERNAL_TENANT_API_BASE = process.env.REACT_APP_TENANT_CONFIG_URL || '';
const TENANT_API_BEARER_TOKEN = process.env.REACT_APP_TENANT_CONFIG_TOKEN || '';
const LOCAL_PROXY_TENANT_API_BASE = '/api/v1/tenant-config';

const isLocalHost = (hostname) => hostname === 'localhost' || hostname === '127.0.0.1';

export const resolveTenantId = () => {
  const url = new URL(window.location.href);
  const localHost = isLocalHost(window.location.hostname);
  const queryTenant = url.searchParams.get('tenant');
  if (queryTenant) {
    if (!localHost) {
      localStorage.setItem('tenantId', queryTenant);
    }
    return queryTenant;
  }

  if (localHost) {
    return DEFAULT_TENANT_THEME.tenantId;
  }

  const storedTenant = localStorage.getItem('tenantId');
  if (storedTenant) return storedTenant;

  const [subdomain] = window.location.hostname.split('.');
  if (subdomain && subdomain !== 'www') {
    return subdomain;
  }

  return DEFAULT_TENANT_THEME.tenantId;
};

const fetchJson = async (url, options = {}) => {
  const response = await fetch(url, options);
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
};

export const fetchTenantConfig = async (tenantId) => {
  const apiCandidates = [
    EXTERNAL_TENANT_API_BASE,
    LOCAL_PROXY_TENANT_API_BASE
  ].filter(Boolean);

  for (const apiBase of apiCandidates) {
    try {
      const apiConfig = await fetchJson(`${apiBase}?tenantId=${encodeURIComponent(tenantId)}`, {
        headers: {
          Accept: 'application/json',
          'X-Tenant-Id': tenantId,
          ...(TENANT_API_BEARER_TOKEN ? { Authorization: `Bearer ${TENANT_API_BEARER_TOKEN}` } : {})
        }
      });
      return validateTenantTheme(apiConfig);
    } catch (error) {
      logger.warn(`Tenant API fallback triggered for '${tenantId}' from '${apiBase}':`, error?.message || error);
    }
  }

  try {
    const mockConfig = await fetchJson(`/tenant-config/${tenantId}.json`);
    return validateTenantTheme(mockConfig);
  } catch (_secondError) {
    try {
      const defaultMock = await fetchJson('/tenant-config/default.json');
      return validateTenantTheme(defaultMock);
    } catch (_thirdError) {
      logger.warn(`Falling back to default tenant theme for '${tenantId}'.`);
      return DEFAULT_TENANT_THEME;
    }
  }
};

export const checkTenantConfigHealth = async () => {
  const healthCandidates = [
    EXTERNAL_TENANT_API_BASE ? EXTERNAL_TENANT_API_BASE.replace(/\/api\/v1\/tenant-config\/?$/, '/health') : '',
    '/health'
  ].filter(Boolean);

  for (const healthUrl of healthCandidates) {
    try {
      const response = await fetch(healthUrl, {
        headers: { Accept: 'application/json' }
      });

      if (response.ok) {
        return {
          healthy: true,
          source: healthUrl,
          details: await response.json()
        };
      }
    } catch (_error) {
      // Continue trying next health endpoint.
    }
  }

  return {
    healthy: false,
    source: null,
    details: null
  };
};
